import * as React from 'react';
import { FC } from 'react';
import {
  Badge,
  Button,
  Gray20,
  Gray100,
  White,
  Black,
  BlackOpacity07,
  Skeleton,
  typeCompact12MediumProperties,
  typeCompact14MediumProperties,
  EmptyState,
  BlackOpacity04,
  typeCompact14RegularProperties,
  Tooltip,
} from '@traceair/tds';
import { classes, stylesheet, media, style } from 'typestyle';
import { BREAKPOINT_SMALL, BREAKPOINT_MEDIUM, containerStyles } from '../../layout/styles';
import { Header } from '../../layout/header/Header';
import { ExpandLessIcon, ExpandMoreIcon, ListIcon, PlusIcon } from '@traceair/webapp-icons';
import getPilotApiService from '../../../api_services/PilotApiService';
import Logger from '../../../libs/logger/Logger';
import { Invoice } from '../../../api_services/types';

import { InvoiceSubmission } from './types';
import {
  formatApiDate,
  formatApiDateShort,
  formatPeriod,
  mapInvoiceStatus,
  getStatusBadgeProps,
} from './InvoicingUtils';

type SortColumn = 'submitted' | 'total';
type SortDirection = 'ASC' | 'DESC';

type InvoicingListViewProps = {
  onSubmitInvoice?: () => void;
  onInvoiceClick?: (invoice: InvoiceSubmission) => void;
};

type StatusCellProps = { status: InvoiceSubmission['status']; paidDate?: string };

const StatusCell: FC<StatusCellProps> = ({ status, paidDate }) => {
  const badge = <Badge label={status} size="l" {...getStatusBadgeProps(status)} />;
  if (status === 'Paid' && paidDate) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {badge}
        <span className={st.paidDate}>{paidDate}</span>
      </span>
    );
  }
  return badge;
};

const st = stylesheet({
  // ── Desktop table ──────────────────────────────────────────────────────────
  tableWrap: {
    display: 'block',
    ...media({ maxWidth: BREAKPOINT_SMALL }, { display: 'none' }),
  },
  tableCard: {
    backgroundColor: White,
    borderRadius: 12,
    border: `1px solid ${Gray20}`,
    overflow: 'auto',
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: 'minmax(140px,1fr) minmax(190px,1.4fr) 185px 140px minmax(180px,1.5fr) 80px',
    borderBottom: `1px solid ${Gray20}`,
    padding: '8px 16px',
    gap: 4,
    ...media(
      { maxWidth: BREAKPOINT_MEDIUM },
      {
        gridTemplateColumns: 'minmax(110px,1fr) minmax(150px,1.2fr) 160px 140px minmax(140px,1fr) 70px',
        padding: '6px 12px',
      },
    ),
  },
  headerCell: {
    ...typeCompact12MediumProperties,
    color: Gray100,
    padding: '8px 0',
  },
  headerCellSortable: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    cursor: 'pointer',
    userSelect: 'none' as const,
    $nest: { '&:hover': { color: Black } },
  },
  headerCellRight: { justifyContent: 'flex-end' as const },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(140px,1fr) minmax(190px,1.4fr) 185px 140px minmax(180px,1.5fr) 80px',
    alignItems: 'center',
    padding: '0 16px',
    gap: 4,
    height: 52,
    borderBottom: `1px solid ${Gray20}`,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    $nest: {
      '&:last-child': { borderBottom: 'none' },
    },
    ...media(
      { maxWidth: BREAKPOINT_MEDIUM },
      {
        gridTemplateColumns: 'minmax(110px,1fr) minmax(150px,1.2fr) 160px 140px minmax(140px,1fr) 70px',
        padding: '0 12px',
        ...typeCompact12MediumProperties,
      },
    ),
  },
  cell: {
    ...typeCompact14RegularProperties,
    color: Black,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  cellRight: { textAlign: 'right' as const, paddingRight: 4 },

  // ── Mobile cards ───────────────────────────────────────────────────────────
  cardList: {
    display: 'none',
    ...media({ maxWidth: BREAKPOINT_SMALL }, { display: 'flex', flexDirection: 'column', gap: 10 }),
  },
  card: {
    backgroundColor: White,
    border: `1px solid ${Gray20}`,
    borderRadius: 12,
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    $nest: {
      '&:hover': { backgroundColor: BlackOpacity07 },
      '&:active': { backgroundColor: BlackOpacity07 },
    },
  },
  cardTopRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  cardBottomRow: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 8,
    marginTop: 4,
  },
  cardMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  cardMetaText: {
    ...typeCompact12MediumProperties,
    color: Gray100,
  },
  paidDate: {
    color: Gray100,
    ...typeCompact14MediumProperties,
  },
  submitBntContainer: {
    marginRight: 12,
    ...media({ maxWidth: BREAKPOINT_SMALL }, { display: 'none' }),
  },
});

const activeSortStyle = style({ color: Black });

function parseTotalValue(total: string): number {
  return parseFloat(total.replace(/[$,]/g, ''));
}

function parseSubmittedDate(submitted: string): number {
  return new Date(submitted).getTime();
}

function mapApiInvoice(inv: Invoice): InvoiceSubmission {
  const num = (v?: number) => v ?? 0;
  const t = inv.submittedTotals ?? {};
  // The service's own rollup, so this list can't drift from the invoice's real total the way a
  // local sum does (it did: mileage got folded into gcpMaterialsCosts and was counted twice, and
  // additionalImagesCosts wasn't in the sum at all). The per-category fallback is only for an
  // invoice stored before the service began reporting totalAmount.
  const totalCost =
    t.totalAmount ??
    num(t.scanCosts) +
      num(t.panoCosts) +
      num(t.lidarCosts) +
      num(t.timeMaterialsFlightCosts) +
      num(t.gcpServicesCosts) +
      num(t.gcpMaterialsCosts) +
      num(t.additionalImagesCosts) -
      num(t.equipmentLeasingExpenses) +
      num(t.otherCosts);

  const status = mapInvoiceStatus(inv.status);

  const d1 = new Date(inv.startDate);
  const d2 = new Date(inv.endDate);
  const [startDate, endDate] = d1 <= d2 ? [inv.startDate, inv.endDate] : [inv.endDate, inv.startDate];

  const countCat = (cat: string) =>
    inv.associatedMissions.filter((c) => c.category === cat && !c.isFailed).reduce((s, c) => s + c.itemCount, 0);
  const scanQty = countCat('scan');
  const panoQty = countCat('pano');
  const lidarQty = countCat('lidar');
  const gcpQty = countCat('gcp_services');
  const parts: string[] = [];
  if (scanQty > 0) parts.push(`${scanQty} scan${scanQty !== 1 ? 's' : ''}`);
  if (panoQty > 0) parts.push(`${panoQty} pano${panoQty !== 1 ? 's' : ''}`);
  if (lidarQty > 0) parts.push(`${lidarQty} lidar`);
  if (gcpQty > 0) parts.push(`${gcpQty} gcp service${gcpQty !== 1 ? 's' : ''}`);
  if (t.equipmentLeasingExpenses && t.equipmentLeasingExpenses > 0) parts.push('equipment leasing');
  if (t.otherCosts && t.otherCosts > 0) parts.push('other');

  return {
    id: inv.id,
    period: formatPeriod(startDate, endDate),
    invoiceName: inv.invoiceName,
    status,
    paidDate: inv.paidOn ? formatApiDateShort(inv.paidOn) : undefined,
    submitted: formatApiDate(inv.submittedAt ?? startDate),
    contents: parts.join(', ') || '—',
    total: `$${totalCost.toLocaleString()}`,
    rawInvoice: inv,
  };
}

type SortIconProps = { column: SortColumn; sortColumn: SortColumn | null; sortDirection: SortDirection };

const SortIcon: FC<SortIconProps> = ({ column, sortColumn, sortDirection }) => {
  if (sortColumn !== column) return null;
  return sortDirection === 'ASC' ? (
    <ExpandLessIcon color={Black} size={16} />
  ) : (
    <ExpandMoreIcon color={Black} size={16} />
  );
};

const OverflowTooltip: FC<{ text: string; className?: string }> = ({ text, className }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  return (
    <Tooltip title={text} open={open}>
      <div
        ref={ref}
        className={className}
        onMouseEnter={() => {
          if (ref.current && ref.current.scrollWidth > ref.current.clientWidth) setOpen(true);
        }}
        onMouseLeave={() => setOpen(false)}
      >
        {text}
      </div>
    </Tooltip>
  );
};

const InvoicingListView: FC<InvoicingListViewProps> = ({ onSubmitInvoice, onInvoiceClick }) => {
  const isMount = React.useRef(true);
  const [invoicesData, setInvoicesData] = React.useState<InvoiceSubmission[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    async function fetchInvoices() {
      try {
        const { invoices } = await getPilotApiService().getInvoices();
        if (isMount.current) {
          setInvoicesData(invoices.map(mapApiInvoice));
        }
      } catch (err) {
        Logger.error(err as Error | string);
      } finally {
        if (isMount.current) setIsLoading(false);
      }
    }
    fetchInvoices();
    return () => {
      isMount.current = false;
    };
  }, []);

  const [sortColumn, setSortColumn] = React.useState<SortColumn | null>('submitted');
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('DESC');

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortColumn(column);
      setSortDirection('DESC');
    }
  };

  const sortedInvoices = React.useMemo(() => {
    if (!sortColumn) return invoicesData;
    return [...invoicesData].sort((a, b) => {
      const aVal = sortColumn === 'submitted' ? parseSubmittedDate(a.submitted) : parseTotalValue(a.total);
      const bVal = sortColumn === 'submitted' ? parseSubmittedDate(b.submitted) : parseTotalValue(b.total);
      return sortDirection === 'ASC' ? aVal - bVal : bVal - aVal;
    });
  }, [sortColumn, sortDirection, invoicesData]);

  const isEmpty = sortedInvoices.length === 0;

  const emptyState = (
    <EmptyState
      icon={<ListIcon size={20} color={Black} />}
      description="Create your first invoice to get started"
      primaryButtonProps={{
        text: 'Submit Invoice',
        onClick: onSubmitInvoice,
        size: 's',
      }}
      size="s"
      className={style({ margin: '0 auto', padding: '48px 0' })}
      style="neutral"
      title="No invoices yet"
    />
  );

  return (
    <div className={containerStyles.container}>
      <Header heading="Invoices">
        {!isEmpty && (
          <div className={st.submitBntContainer}>
            <Button icon={<PlusIcon color={White} />} onClick={onSubmitInvoice} text="Submit Invoice" style="primary" />
          </div>
        )}
      </Header>

      <div className={containerStyles.containerInner}>
        {isLoading ? (
          <div className={st.tableCard}>
            <div className={st.tableHeader}>
              {['Period', 'Invoice ID', 'Status', 'Submitted', 'Contents'].map((h) => (
                <div key={h} className={st.headerCell}>
                  {h}
                </div>
              ))}
              <div key="Total" className={classes(st.headerCell, style({ textAlign: 'right', width: 50 }))}>
                Total
              </div>
            </div>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={st.tableRow} style={{ cursor: 'default' }}>
                <Skeleton width={110} height={14} />
                <Skeleton width={160} height={14} />
                <Skeleton width={76} height={14} />
                <Skeleton width={80} height={14} />
                <Skeleton width={140} height={14} />
                <Skeleton width={50} height={14} />
              </div>
            ))}
          </div>
        ) : isEmpty ? (
          emptyState
        ) : (
          <>
            {/* ── Desktop table ─────────────────────────────────────── */}
            <div className={st.tableWrap}>
              <div className={st.tableCard}>
                <div className={st.tableHeader}>
                  <div className={st.headerCell}>Period</div>
                  <div className={st.headerCell}>Invoice ID</div>
                  <div className={st.headerCell}>Status</div>
                  <div
                    className={classes(
                      st.headerCell,
                      st.headerCellSortable,
                      sortColumn === 'submitted' && activeSortStyle,
                    )}
                    onClick={() => handleSort('submitted')}
                  >
                    Submitted
                    <SortIcon column="submitted" sortColumn={sortColumn} sortDirection={sortDirection} />
                  </div>
                  <div className={st.headerCell}>Contents</div>
                  <div
                    className={classes(
                      st.headerCell,
                      st.headerCellRight,
                      st.headerCellSortable,
                      sortColumn === 'total' && activeSortStyle,
                    )}
                    onClick={() => handleSort('total')}
                  >
                    <SortIcon column="total" sortColumn={sortColumn} sortDirection={sortDirection} />
                    Total
                  </div>
                </div>

                {sortedInvoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className={st.tableRow}
                    onClick={() => onInvoiceClick?.(invoice)}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BlackOpacity04)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}
                  >
                    <OverflowTooltip className={st.cell} text={invoice.period} />
                    <OverflowTooltip className={st.cell} text={invoice.invoiceName} />
                    <div className={st.cell}>
                      <StatusCell status={invoice.status} paidDate={invoice.paidDate} />
                    </div>
                    <div className={st.cell}>{invoice.submitted}</div>
                    <OverflowTooltip className={st.cell} text={invoice.contents} />
                    <div className={classes(st.cell, st.cellRight)}>{invoice.total}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Mobile cards ──────────────────────────────────────── */}
            <div className={st.cardList}>
              {sortedInvoices.map((invoice) => (
                <div key={invoice.id} className={st.card} onClick={() => onInvoiceClick?.(invoice)}>
                  <div className={st.cardTopRow}>
                    <span>{invoice.period}</span>
                    <StatusCell status={invoice.status} paidDate={invoice.paidDate} />
                  </div>
                  <span>{invoice.contents}</span>
                  <div className={st.cardBottomRow}>
                    <div className={st.cardMeta}>
                      <span className={st.cardMetaText}>
                        Created: {formatApiDateShort(invoice.rawInvoice.submittedAt ?? invoice.rawInvoice.startDate)}
                      </span>
                      <span className={st.cardMetaText}>{invoice.invoiceName}</span>
                    </div>
                    <span>{invoice.total}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InvoicingListView;
